import type { TableColumn, TableRow } from '@/features/dashboard/api/schemas'

type Cell = TableRow['cells'][number]

const SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' }
const MINUS = '−'

/** The tone a change reads in: `flat` is no change, `neutral` is not a change at all. */
export type CellTone = 'positive' | 'negative' | 'flat' | 'neutral'

const grouped = (abs: number, decimals: number) =>
  abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

const isPercent = (column: TableColumn) => column.format?.style === 'percent'

/** Shares show whole, changes of a percentage one decimal; an integer shows whole, any other number keeps one. */
const decimalsOf = (column: TableColumn, value: number) =>
  column.format?.decimals ??
  (column.kind === 'share'
    ? 0
    : column.kind === 'deltaPercent'
      ? 1
      : Number.isInteger(value)
        ? 0
        : 1)

/** What the stored number is multiplied by to be shown: fractions are shown as percentages. */
const factorOf = (column: TableColumn) =>
  column.kind === 'share' || column.kind === 'deltaPercent' || isPercent(column)
    ? 100
    : 1

// Percent columns hold fractions.
const magnitude = (
  abs: number,
  column: TableColumn,
  unit: string | null | undefined,
  decimals: number
) => {
  const style = column.format?.style ?? 'number'
  if (style === 'percent') return `${grouped(abs * 100, decimals)}%`
  const symbol =
    style === 'currency' && unit ? (SYMBOLS[unit] ?? `${unit} `) : ''
  return `${symbol}${grouped(abs, decimals)}`
}

const isZeroAt = (abs: number, factor: number, decimals: number) =>
  Number((abs * factor).toFixed(decimals)) === 0

/** What a cell reads: values are only formatted, never derived. */
export const formatCell = (
  cell: Cell,
  column: TableColumn,
  unit?: string | null
): string => {
  if (cell === null) return column.kind === 'deltaPercent' ? 'n/m' : '–'
  if (typeof cell === 'string') return cell
  const abs = Math.abs(cell)
  const decimals = decimalsOf(column, cell)
  const zero = isZeroAt(abs, factorOf(column), decimals)
  const change = zero ? '' : cell > 0 ? '+' : MINUS

  if (column.kind === 'share') return `${grouped(abs * 100, decimals)}%`
  if (column.kind === 'deltaPercent') {
    return `${change}${grouped(abs * 100, decimals)}%`
  }
  if (column.kind === 'delta') {
    // A change in a percent column is in percentage points.
    const text = isPercent(column)
      ? `${grouped(abs * 100, decimals)}\u00a0ppt`
      : magnitude(abs, column, unit, decimals)
    return `${change}${text}`
  }
  const sign = cell < 0 && !zero ? MINUS : ''
  return `${sign}${magnitude(abs, column, unit, decimals)}`
}

export const cellTone = (cell: Cell, column: TableColumn): CellTone => {
  if (
    typeof cell !== 'number' ||
    (column.kind !== 'delta' && column.kind !== 'deltaPercent')
  ) {
    return 'neutral'
  }
  if (isZeroAt(Math.abs(cell), factorOf(column), decimalsOf(column, cell)))
    return 'flat'
  const good = column.lowerIsBetter === true ? cell < 0 : cell > 0
  return good ? 'positive' : 'negative'
}

/** `bar` scales against the largest figure, `diverge` about the average. */
export type BarScale = {
  mode: 'bar' | 'diverge'
  centre: number
  extent: number
}

/** A `display` column's scale, from its plain rows only (a total or group is a sum). */
export const barScale = (
  rows: TableRow[],
  column: TableColumn,
  index: number
): BarScale | null => {
  if (column.display === undefined) return null
  const values = rows
    .filter(row => row.kind === 'row')
    .map(row => row.cells[index])
    .filter((cell): cell is number => typeof cell === 'number')
  if (values.length === 0) return null
  if (column.display === 'bar') {
    return { mode: 'bar', centre: 0, extent: Math.max(...values.map(Math.abs)) }
  }
  const centre = values.reduce((sum, v) => sum + v, 0) / values.length
  return {
    mode: 'diverge',
    centre,
    extent: Math.max(...values.map(v => Math.abs(v - centre))),
  }
}

/** A cell's bar as a share of the track, from its start (`bar`) or its middle (`diverge`). */
export const barGeometry = (
  cell: number,
  scale: BarScale
): { left: number; width: number; below: boolean } => {
  if (scale.extent === 0)
    return { left: scale.mode === 'bar' ? 0 : 50, width: 0, below: false }
  if (scale.mode === 'bar') {
    return {
      left: 0,
      width: (Math.abs(cell) / scale.extent) * 100,
      below: false,
    }
  }
  const gap = ((cell - scale.centre) / scale.extent) * 50
  return { left: gap < 0 ? 50 + gap : 50, width: Math.abs(gap), below: gap < 0 }
}
